import Vue from 'vue';
import Vuex, { ActionTree, GetterTree, MutationTree } from 'vuex';
import DoceboAPI from '@/lib/DoceboAPI';
import HealthForceGoAPI from '@/lib/HealthForceGoAPI';
import getHashParams from '@/lib/getHashParams';
import { NXWConfig } from '@/types';
import * as audio from '@/store/audio';
import * as skills from '@/store/skills';
import * as catalogues from '@/store/catalogues';
import * as buildings from '@/store/buildings';
import * as enrolments from '@/store/enrolments';
import * as sessions from '@/store/sessions';
import * as course from '@/store/course';
import * as learningPlan from '@/store/learningPlan';
import * as user from '@/store/user';

interface LocalState {
  showLoadingOverlay: boolean;
  isHighFidelity: boolean | null;
  config: NXWConfig;
}

Vue.use(Vuex);

const configLocation = '/config.json';

const doceboAPI = DoceboAPI.instance;

const hfgAPI = HealthForceGoAPI.instance;

hfgAPI.setEnvironment(getHashParams().host);

const localState: LocalState = {
  showLoadingOverlay: false,
  isHighFidelity: null,
  config: {} as NXWConfig,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getters: GetterTree<LocalState, any> = {};

const mutations: MutationTree<LocalState> = {
  SET_LOADING_OVERLAY: (state, visible: boolean | string) => {
    Vue.set(state, 'showLoadingOverlay', visible);
  },

  SET_FIDELITY_LEVEL: (state, isHighFidelity: boolean) => {
    Vue.set(state, 'isHighFidelity', isHighFidelity);
  },

  SET_CONFIG: (state, config: NXWConfig) => {
    Vue.set(state, 'config', config);
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const actions: ActionTree<LocalState, any> = {
  SET_LOADING_OVERLAY: (context, visible: boolean | string) => {
    return context.commit('SET_LOADING_OVERLAY', visible);
  },

  SET_FIDELITY_LEVEL: (context, isHighFidelity: boolean) => {
    return context.commit('SET_FIDELITY_LEVEL', isHighFidelity);
  },

  INITIALISE: (context, jwt: string): Promise<void> => {
    return fetch(configLocation)
      .then((response) => response.json())
      .then((config: NXWConfig) => {
        context.commit('SET_CONFIG', config);
        doceboAPI.setHostname(config.doceboHostname);
        hfgAPI.setToken(jwt);
        context.dispatch('user/SET_BRANCH_GROUPS', config.branchGroups, {
          root: true,
        });
        return context.dispatch('REFRESH_DOCEBO_TOKEN');
      })
      .then(() => doceboAPI.loginUserWithAccessToken()) // Log user into Docebo to enable course tracking
      .then(() => doceboAPI.initUserSessionInfo()) // Store user ID
      .then(() =>
        doceboAPI
          .getUserInfo()
          .then((userInfo) => context.dispatch('user/SET_USER_INFO', userInfo))
      ); // Store user properties
  },

  REFRESH_DOCEBO_TOKEN: (): Promise<void> => {
    return hfgAPI
      .getCourseData()
      .then((data) => doceboAPI.initToken(data.doceboToken));
  },
};

export default new Vuex.Store({
  state: localState,
  getters,
  mutations,
  actions,
  modules: {
    audio,
    skills,
    catalogues,
    buildings,
    enrolments,
    sessions,
    course,
    learningPlan,
    user,
  },
});
